/**
 * Pure logic for the automations feature: constants, condition types, and the
 * helpers that map/describe/validate condition rows. Kept free of React and
 * data-fetching so it can be unit-tested in isolation and reused by the
 * automation UI components. The hooks in src/hooks/use-automations.ts build on
 * top of these primitives.
 */
import { US_STATE_OPTIONS, US_STATE_CODES } from '@/lib/us-states';
import type { AutomationCondition, AutomationRule } from '@/api/generated/client';

export const SOURCE_LABELS: Record<string, string> = {
  manual: 'In-app',
  claimpredict: 'ClaimPredict',
  widget: 'Embed Widget',
};

export const RUN_STATUS_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  success: 'default',
  skipped: 'secondary',
  failed: 'destructive',
  pending: 'outline',
};

const RUN_REASON_LABELS: Record<string, string> = {
  NO_RECIPIENT_EMAIL: 'No homeowner email on file',
  CONDITIONS_NOT_MET: 'Conditions not met',
  NO_CLAIM: 'No claim on event',
  CLAIM_NOT_FOUND: 'Claim not found',
  UNSUPPORTED_ACTION: 'Unsupported action',
  ACTION_FAILED: 'Action failed',
};

/** Only company owners and admins can view or manage automations. */
export function canManageAutomationsForRole(role?: string): boolean {
  return role === 'admin' || role === 'owner';
}

export type ConditionField = 'source' | 'feePercentage' | 'state';
export type ConditionOperator = 'eq' | 'neq' | 'gt' | 'lt';

/** Max conditions per rule — mirrors the backend cap (AND semantics). */
export const MAX_CONDITIONS = 5;

export const CONDITION_FIELD_LABELS: Record<ConditionField, string> = {
  source: 'Source',
  feePercentage: 'Fee',
  state: 'State',
};

export const SOURCE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'claimpredict', label: 'ClaimPredict' },
  { value: 'widget', label: 'Embed Widget' },
  { value: 'manual', label: 'In-app' },
];

/** Operators available per field. Only feePercentage supports comparisons. */
export const OPERATOR_OPTIONS: Record<ConditionField, Array<{ value: ConditionOperator; label: string }>> = {
  source: [
    { value: 'eq', label: 'is' },
    { value: 'neq', label: 'is not' },
  ],
  state: [
    { value: 'eq', label: 'is' },
    { value: 'neq', label: 'is not' },
  ],
  feePercentage: [
    { value: 'eq', label: 'is' },
    { value: 'neq', label: 'is not' },
    { value: 'gt', label: 'is greater than' },
    { value: 'lt', label: 'is less than' },
  ],
};

// Re-exported from the canonical list so existing importers keep working.
export { US_STATE_OPTIONS };

export function newConditionId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `condition-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export interface ConditionFormState {
  id: string;
  field: ConditionField;
  operator: ConditionOperator;
  value: string;
}

export interface RuleFormState {
  name: string;
  conditions: ConditionFormState[];
  formTemplateId: string;
  isActive: boolean;
}

export const EMPTY_FORM: RuleFormState = {
  name: '',
  conditions: [],
  formTemplateId: '',
  isActive: true,
};

/** Maps a form condition row to the strictly-typed API condition union. */
export function toApiCondition(condition: ConditionFormState): AutomationCondition {
  switch (condition.field) {
    case 'feePercentage':
      return { field: 'feePercentage', operator: condition.operator, value: Number(condition.value) };
    case 'source':
      return {
        field: 'source',
        operator: condition.operator as 'eq' | 'neq',
        value: condition.value.trim() as 'manual' | 'claimpredict' | 'widget',
      };
    case 'state':
      return { field: 'state', operator: condition.operator as 'eq' | 'neq', value: condition.value.trim() };
  }
}

/** A sensible default condition row for a newly selected field. */
export function defaultConditionFor(field: ConditionField): ConditionFormState {
  switch (field) {
    case 'feePercentage':
      return { id: newConditionId(), field, operator: 'eq', value: '' };
    case 'state':
      return { id: newConditionId(), field, operator: 'eq', value: 'CA' };
    case 'source':
    default:
      return { id: newConditionId(), field, operator: 'eq', value: 'claimpredict' };
  }
}

/** Whether a single in-progress condition row is complete and valid. */
export function isConditionValid(condition: ConditionFormState): boolean {
  switch (condition.field) {
    case 'source':
      return SOURCE_OPTIONS.some((option) => option.value === condition.value);
    case 'state':
      return US_STATE_CODES.has(condition.value.trim().toUpperCase());
    case 'feePercentage': {
      const numeric = Number(condition.value);
      return condition.value.trim() !== '' && Number.isFinite(numeric) && numeric >= 0 && numeric <= 100;
    }
    default:
      return false;
  }
}

function describeCondition(condition: { field: string; operator: string; value: string | number }): string {
  if (condition.field === 'source') {
    const label = SOURCE_LABELS[String(condition.value)] ?? String(condition.value);
    return condition.operator === 'eq' ? `the claim came from ${label}` : `the claim did not come from ${label}`;
  }
  if (condition.field === 'feePercentage') {
    const opText =
      OPERATOR_OPTIONS.feePercentage.find((option) => option.value === condition.operator)?.label ?? condition.operator;
    return `the fee percentage ${opText} ${condition.value}%`;
  }
  if (condition.field === 'state') {
    return condition.operator === 'eq'
      ? `the property state is ${condition.value}`
      : `the property state is not ${condition.value}`;
  }
  return '';
}

export function describeAutomationRule(rule: AutomationRule, templateName?: string): string {
  const parts = (rule.conditions ?? []).map(describeCondition).filter(Boolean);
  const conditionText = parts.length > 0 ? ` and ${parts.join(' and ')}` : '';
  const formText = templateName ? `"${templateName}"` : `form #${rule.actionConfig.formTemplateId}`;
  return `When a claim is created${conditionText}, send ${formText} to the homeowner's email.`;
}

export function formatAutomationRunReason(reason?: unknown): string | null {
  if (!reason) return null;
  const normalizedReason = String(reason);
  return RUN_REASON_LABELS[normalizedReason] ?? normalizedReason;
}
